import React, { useMemo } from 'react';

type Op =
    | { kind: 'unchanged'; before: number; after: number; text: string }
    | { kind: 'removed'; before: number; text: string }
    | { kind: 'added'; after: number; text: string };

type Row =
    | { kind: 'context'; before: number; after: number; text: string }
    | {
          kind: 'change';
          beforeLine: number | null;
          beforeText: string | null;
          afterLine: number | null;
          afterText: string | null;
      };

/**
 * Splits on `\n` and strips a trailing `\r` from each line. Mixed line
 * endings (a CRLF original vs an LF edit) would otherwise mark every line
 * different — invisible `\r` characters defeat string equality.
 */
function splitLines(text: string): string[] {
    return text.split('\n').map((line) => line.replace(/\r$/, ''));
}

/**
 * If both sides parse as JSON, pretty-print them (2-space indent) before
 * diffing. Without this, a one-character edit in a minified single-line JSON
 * blob shows up as "1 line removed, N lines added" if the new draft was
 * pretty-printed (or vice versa), which is useless for review.
 *
 * If either side fails to parse, both sides are returned unchanged so plain
 * text and broken JSON diffs still work.
 */
function maybeFormatJsonPair(before: string, after: string): { before: string; after: string } {
    try {
        const parsedBefore = JSON.parse(before);
        const parsedAfter = JSON.parse(after);
        if (
            parsedBefore !== null &&
            typeof parsedBefore === 'object' &&
            parsedAfter !== null &&
            typeof parsedAfter === 'object'
        ) {
            return {
                before: JSON.stringify(parsedBefore, null, 2),
                after: JSON.stringify(parsedAfter, null, 2),
            };
        }
    } catch {
        // Not JSON, or one side doesn't parse — fall through.
    }
    return { before, after };
}

/**
 * Standard LCS-based line diff. O(n*m). Fine for the few hundred lines we see
 * in prompt content; no external dep.
 */
function lineDiff(beforeText: string, afterText: string): Op[] {
    const a = splitLines(beforeText);
    const b = splitLines(afterText);
    const n = a.length;
    const m = b.length;
    const dp: number[][] = Array.from({ length: n + 1 }, () =>
        new Array(m + 1).fill(0),
    );
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
            else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }
    const out: Op[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            out.push({ kind: 'unchanged', before: i + 1, after: j + 1, text: a[i] });
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            out.push({ kind: 'removed', before: i + 1, text: a[i] });
            i++;
        } else {
            out.push({ kind: 'added', after: j + 1, text: b[j] });
            j++;
        }
    }
    while (i < n) {
        out.push({ kind: 'removed', before: i + 1, text: a[i] });
        i++;
    }
    while (j < m) {
        out.push({ kind: 'added', after: j + 1, text: b[j] });
        j++;
    }
    return out;
}

/**
 * Pairs consecutive `removed` + `added` ops into change blocks aligned
 * side-by-side. WebStorm-style: a deletion on the left lines up with its
 * paired addition on the right; if the block is uneven, the shorter side
 * gets blank padding rows.
 */
function pairOps(ops: Op[]): Row[] {
    const rows: Row[] = [];
    let i = 0;
    while (i < ops.length) {
        const op = ops[i];
        if (op.kind === 'unchanged') {
            rows.push({
                kind: 'context',
                before: op.before,
                after: op.after,
                text: op.text,
            });
            i++;
            continue;
        }
        // Collect a change block: contiguous removed+added.
        const removed: Op[] = [];
        const added: Op[] = [];
        while (i < ops.length && ops[i].kind !== 'unchanged') {
            if (ops[i].kind === 'removed') removed.push(ops[i]);
            else added.push(ops[i]);
            i++;
        }
        const pairs = Math.max(removed.length, added.length);
        for (let p = 0; p < pairs; p++) {
            const r = removed[p];
            const a = added[p];
            rows.push({
                kind: 'change',
                beforeLine: r ? (r as { before: number }).before : null,
                beforeText: r ? r.text : null,
                afterLine: a ? (a as { after: number }).after : null,
                afterText: a ? a.text : null,
            });
        }
    }
    return rows;
}

interface Props {
    before: string;
    after: string;
    /** Hide unchanged lines that are far from any change. Defaults to 3. */
    contextLines?: number;
    /** Cap on rendered rows. Defaults to 4000. */
    maxRows?: number;
}

/**
 * Hide unchanged blocks that stretch beyond `contextLines` either side of a
 * change, replacing them with a "… N unchanged lines …" collapse marker.
 */
function collapseUnchanged(rows: Row[], context: number): Array<Row | { kind: 'gap'; count: number }> {
    if (context < 0) return rows;
    const out: Array<Row | { kind: 'gap'; count: number }> = [];
    let i = 0;
    while (i < rows.length) {
        const row = rows[i];
        if (row.kind !== 'context') {
            out.push(row);
            i++;
            continue;
        }
        // Count how many context rows in a row.
        let runEnd = i;
        while (runEnd < rows.length && rows[runEnd].kind === 'context') runEnd++;
        const runLen = runEnd - i;
        const isFirstRun = i === 0;
        const isLastRun = runEnd === rows.length;
        // For an internal run with N context rows, keep `context` at start and
        // `context` at end; collapse the middle if it's > 2 * context.
        if (!isFirstRun && !isLastRun && runLen > 2 * context) {
            for (let k = 0; k < context; k++) out.push(rows[i + k]);
            out.push({ kind: 'gap', count: runLen - 2 * context });
            for (let k = runLen - context; k < runLen; k++) out.push(rows[i + k]);
        } else if (isFirstRun && runLen > context) {
            out.push({ kind: 'gap', count: runLen - context });
            for (let k = runLen - context; k < runLen; k++) out.push(rows[i + k]);
        } else if (isLastRun && runLen > context) {
            for (let k = 0; k < context; k++) out.push(rows[i + k]);
            out.push({ kind: 'gap', count: runLen - context });
        } else {
            for (let k = 0; k < runLen; k++) out.push(rows[i + k]);
        }
        i = runEnd;
    }
    return out;
}

const PANE_BG_REMOVED = '#fff1f0';
const PANE_BG_ADDED = '#f6ffed';
const PANE_BG_PADDING = '#fafafa';

export const SideBySideLineDiff: React.FC<Props> = ({
    before,
    after,
    contextLines = 3,
    maxRows = 4000,
}) => {
    const { rendered, removedCount, addedCount, totalRows, jsonFormatted } = useMemo(() => {
        const formatted = maybeFormatJsonPair(before ?? '', after ?? '');
        const ops = lineDiff(formatted.before, formatted.after);
        const rows = pairOps(ops);
        const collapsed = collapseUnchanged(rows, contextLines);
        const removedCount = ops.filter((o) => o.kind === 'removed').length;
        const addedCount = ops.filter((o) => o.kind === 'added').length;
        return {
            rendered: collapsed.slice(0, maxRows),
            removedCount,
            addedCount,
            totalRows: collapsed.length,
            jsonFormatted: formatted.before !== (before ?? '') || formatted.after !== (after ?? ''),
        };
    }, [before, after, contextLines, maxRows]);

    const truncated = totalRows > maxRows;

    return (
        <div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                <span style={{ color: '#ff4d4f' }}>−{removedCount} removed</span>{' '}
                <span style={{ color: '#52c41a' }}>+{addedCount} added</span>
                {jsonFormatted && (
                    <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                        (both sides pretty-printed for comparison)
                    </span>
                )}
                {truncated && (
                    <span style={{ marginLeft: 8 }}>
                        (showing first {maxRows} of {totalRows} rows)
                    </span>
                )}
            </div>
            <div
                style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                    background: '#fff',
                    maxHeight: 540,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    lineHeight: 1.55,
                }}
            >
                {rendered.map((row, idx) => {
                    if (row.kind === 'gap') {
                        return (
                            <div
                                key={idx}
                                style={{
                                    background: '#f5f5f5',
                                    color: '#8c8c8c',
                                    padding: '4px 12px',
                                    textAlign: 'center',
                                    fontStyle: 'italic',
                                    borderTop: '1px solid #f0f0f0',
                                    borderBottom: '1px solid #f0f0f0',
                                }}
                            >
                                … {row.count} unchanged line{row.count === 1 ? '' : 's'} …
                            </div>
                        );
                    }
                    if (row.kind === 'context') {
                        return (
                            <DiffRow
                                key={idx}
                                leftNumber={row.before}
                                leftText={row.text}
                                leftBg="transparent"
                                rightNumber={row.after}
                                rightText={row.text}
                                rightBg="transparent"
                            />
                        );
                    }
                    return (
                        <DiffRow
                            key={idx}
                            leftNumber={row.beforeLine}
                            leftText={row.beforeText}
                            leftBg={row.beforeText !== null ? PANE_BG_REMOVED : PANE_BG_PADDING}
                            rightNumber={row.afterLine}
                            rightText={row.afterText}
                            rightBg={row.afterText !== null ? PANE_BG_ADDED : PANE_BG_PADDING}
                        />
                    );
                })}
            </div>
        </div>
    );
};

const DiffRow: React.FC<{
    leftNumber: number | null;
    leftText: string | null;
    leftBg: string;
    rightNumber: number | null;
    rightText: string | null;
    rightBg: string;
}> = ({ leftNumber, leftText, leftBg, rightNumber, rightText, rightBg }) => {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 40px 1fr',
            }}
        >
            <span
                style={{
                    color: '#bfbfbf',
                    textAlign: 'right',
                    paddingRight: 8,
                    background: leftBg,
                    userSelect: 'none',
                    borderRight: '1px solid #f0f0f0',
                }}
            >
                {leftNumber ?? ''}
            </span>
            <span
                style={{
                    background: leftBg,
                    paddingLeft: 8,
                    paddingRight: 8,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    borderRight: '1px solid #f0f0f0',
                }}
            >
                {leftText ?? ''}
            </span>
            <span
                style={{
                    color: '#bfbfbf',
                    textAlign: 'right',
                    paddingRight: 8,
                    background: rightBg,
                    userSelect: 'none',
                    borderRight: '1px solid #f0f0f0',
                }}
            >
                {rightNumber ?? ''}
            </span>
            <span
                style={{
                    background: rightBg,
                    paddingLeft: 8,
                    paddingRight: 8,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {rightText ?? ''}
            </span>
        </div>
    );
};
